import { Brain, Coins, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { pointsForUsage, formatPoints, type UsageLike } from "@/lib/tokenEstimate";

interface MessageCostBreakdownProps {
  model: string;
  usage: UsageLike & { total_tokens?: number };
}

/**
 * Per-message cost strip rendered under each assistant reply.
 *
 * Shows token usage from the gateway (input / output / reasoning) plus the
 * points charged for this turn. Raw USD spend is intentionally NOT shown —
 * we surface only the opaque point total so users can see how much of their
 * Pro allowance / top-up balance the message used without comparison-shopping
 * against raw API prices. Points are computed with the locked
 * 1.5× margin × 1.10 platform fee formula so every charge is profitable.
 */
const MessageCostBreakdown = ({ model, usage }: MessageCostBreakdownProps) => {
  const inTok = usage.prompt_tokens ?? 0;
  const outTok = usage.completion_tokens ?? 0;
  const reasonTok = usage.completion_tokens_details?.reasoning_tokens ?? 0;
  const points = pointsForUsage(model, usage);

  if (inTok + outTok === 0) return null;

  return (
    <div
      className="mt-2 pt-2 border-t border-border/40 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono text-muted-foreground"
      aria-label="Message cost breakdown"
    >
      <span
        className="inline-flex items-center gap-1 text-foreground"
        title={`${points.toLocaleString()} points charged for this reply`}
      >
        <Coins className="w-3 h-3 text-primary" />
        {formatPoints(points)} pts
      </span>
      {inTok > 0 && (
        <span className="inline-flex items-center gap-1" title={`${inTok.toLocaleString()} input tokens`}>
          <ArrowUpFromLine className="w-3 h-3" />
          in {inTok.toLocaleString()}
        </span>
      )}
      {outTok > 0 && (
        <span className="inline-flex items-center gap-1" title={`${outTok.toLocaleString()} output tokens`}>
          <ArrowDownToLine className="w-3 h-3" />
          out {outTok.toLocaleString()}
        </span>
      )}
      {reasonTok > 0 && (
        <span
          className="inline-flex items-center gap-1 text-primary/80"
          title={`${reasonTok.toLocaleString()} reasoning tokens (included in output)`}
        >
          <Brain className="w-3 h-3" />
          reasoned {reasonTok.toLocaleString()}
        </span>
      )}
    </div>
  );
};

export default MessageCostBreakdown;